import { useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { FolderUp } from "lucide-react"

import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { ScrollArea } from "@/components/ui/scroll-area.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import type { TagPath } from "@/types/dataPointTypes.ts"

import { getTags } from "../../api/getTags.ts"
import { getFullTagPath } from "../../util/getFullTagPath.ts"
import { getTagFolder } from "../../util/getTagFolder.ts"

import TagPathDisplay from "./TagPathDisplay.tsx"

export default function TagBrowser({
  value,
  onChange,
}: {
  value: TagPath
  onChange: (path: TagPath) => void
}) {
  const [req, setReq] = useState<TagPath>(getTagFolder(value || {}))
  const [filter, setFilter] = useState<string>("")
  const folder = getTagFolder(req)
  const { data, isPending } = useQuery({
    queryKey: ["tagBrowser", { req }],
    queryFn: () => getTags(req),
  })

  const filtered: TagPath[] = useMemo(() => {
    if (data != undefined) {
      return data.filter((dp) => getFullTagPath(dp).toLowerCase().includes(filter.toLowerCase()))
    } else {
      return []
    }
  }, [data, filter])

  const handleSelect = (path: TagPath) => {
    if (path.folder) {
      setFilter("")
      setReq(path)
    } else {
      onChange(path)
    }
  }

  return (
    <div className="h-[470px] overflow-y-hidden border rounded-md bg-muted flex flex-col">
      <div className="p-4">
        <Input
          value={filter}
          placeholder="Search tags"
          onChange={(e) => setFilter(e.target.value)}
          className="bg-white"
        />
      </div>
      <div className="overflow-y-hidden">
        <ScrollArea className="h-full p-4 pt-0">
          <div className="flex flex-col gap-2">
            {isPending &&
              [...Array(10).keys()].map((i) => (
                <Skeleton className="w-full h-[36px] bg-white" key={i} />
              ))}
            {req.historyProvider != undefined && (
              <Button
                variant="outline"
                className="justify-baseline italic sticky top-0"
                type="button"
                onClick={() => setReq(folder)}
              >
                <FolderUp />
                {getFullTagPath(req)}
              </Button>
            )}
            {filtered.map((path, i) => (
              <TagPathDisplay
                path={path}
                key={i}
                onSelect={handleSelect}
                selected={value ? path.tag == value.tag : false}
              />
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
